import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import { getParentTrainerId } from "@fitness-app/app-store/src/store/reducers/user/selectors";

import { type ResolvedRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { ALL_PRODUCTS_ITEM_ID } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import {
  aggregateNewClients,
  monthKeysOfRange,
  type NewClientsMonthStatsRow,
} from "~/modules/Dashboard/AppStats/newClients/aggregateNewClients";
import { supabase, useAppSelector } from "~/store/initializeStore";

const fetchMonthStats = async (creator: string, months: string[]) => {
  const { data } = await supabase
    .from("product_month_stats")
    .select("monthYear, product, type, byDay, productData:product(name, type)")
    .eq("creator", creator)
    .in("monthYear", months)
    .overrideTypes<NewClientsMonthStatsRow[]>()
    .throwOnError();

  return data;
};

/** New clients per day for the selected range and its comparison window, from `product_month_stats`. */
export const useNewClientsStats = (
  products: string[],
  range: ResolvedRange | null,
  comparisonRange: ResolvedRange | null,
) => {
  const creator = useAppSelector(getParentTrainerId);

  const months = useMemo(() => {
    const keys = new Set<string>();
    for (const window of [range, comparisonRange]) {
      if (window) {
        monthKeysOfRange(window).forEach((key) => keys.add(key));
      }
    }
    return [...keys].sort();
  }, [range, comparisonRange]);

  const { data, isLoading } = useQuery(["useNewClientsStats", creator, months], {
    enabled: !!creator && months.length > 0,
    keepPreviousData: true,
    queryFn: () => fetchMonthStats(creator, months),
  });

  const productIds = useMemo(
    () => (products.length === 1 && products[0] === ALL_PRODUCTS_ITEM_ID ? null : new Set(products)),
    [products],
  );

  const current = useMemo(
    () => (range ? aggregateNewClients(data ?? [], range, productIds) : null),
    [data, range, productIds],
  );
  const previous = useMemo(
    () => (comparisonRange ? aggregateNewClients(data ?? [], comparisonRange, productIds) : null),
    [data, comparisonRange, productIds],
  );

  return { current, previous, isLoading };
};
